import { useState } from 'react';

export default function TenantsLeasesHero() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    numberOfUnits: '',
    occupancyRate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qpapqhs78ip5370aq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          numberOfUnits: '',
          occupancyRate: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-white to-slate-50 pt-32 pb-20">
      <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 via-transparent to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-file-list-3-line"></i>
              <span>Expert Tenant & Lease Management</span>
            </div>
            
            <h1 className="text-5xl font-bold text-slate-900 mb-6 leading-tight">
              How to Deal with Tenants and Leases When Selling Your Apartment Complex
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Navigate tenant relationships, lease assignments, and legal requirements with confidence. Get expert guidance on managing occupied properties during the sale process.
            </p>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 mb-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">What You'll Learn:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                  <span className="text-slate-700">Understanding tenant rights and legal obligations during property sales</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                  <span className="text-slate-700">Effective communication strategies with current tenants</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                  <span className="text-slate-700">Lease assignment process and documentation requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                  <span className="text-slate-700">Handling security deposits, rent rolls, and estoppel certificates</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                  <span className="text-slate-700">Strategies for dealing with problem tenants and lease expirations</span>
                </li>
              </ul>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-teal-600"></i>
                <span>15 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-article-line text-teal-600"></i>
                <span>3,600+ words</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-user-star-line text-teal-600"></i>
                <span>Expert guidance</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Get Expert Tenant Management Consultation</h2>
              <p className="text-slate-600">Free consultation on handling tenants and leases during your sale</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="tenants-leases-hero-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-medium text-slate-700 mb-1">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, City, State"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="numberOfUnits" className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Units
                  </label>
                  <input
                    type="number"
                    id="numberOfUnits"
                    name="numberOfUnits"
                    value={formData.numberOfUnits}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="12"
                  />
                </div>

                <div>
                  <label htmlFor="occupancyRate" className="block text-sm font-medium text-slate-700 mb-1">
                    Occupancy Rate
                  </label>
                  <select
                    id="occupancyRate"
                    name="occupancyRate"
                    value={formData.occupancyRate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select</option>
                    <option value="100%">100% Occupied</option>
                    <option value="90-99%">90-99% Occupied</option>
                    <option value="75-89%">75-89% Occupied</option>
                    <option value="50-74%">50-74% Occupied</option>
                    <option value="below-50%">Below 50%</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                  Tenant/Lease Concerns
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Describe any specific tenant or lease issues..."
                ></textarea>
                <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  Thank you! We'll contact you within 24 hours to discuss your tenant and lease management needs.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  Something went wrong. Please call us at (555) 123-4567 or try again later.
                </div>
              )}
            </form>

            <p className="text-xs text-slate-500 text-center mt-4">
              Your information is secure and will never be shared with third parties.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
